import { useMemo } from "react";
import AuthorizedImage from "@/components/authorized-image";
import LessonFile from "@/features/content/components/lesson-file";
import AmoozimPlayer from "@/packages/player";
import { AudioPlayer } from "@/features/audio-player";
import type { CourseContent } from "@/services/vitrin-service";
import { STORAGE_PREVIEW_BASE } from "@/utils/constant";

type Props = {
  content: CourseContent;
  lessonTitle?: string;
  analyticsContext?: {
    miniappId?: string;
    courseId?: number;
    lessonId?: number;
  };
};

const resolveAttachmentUrl = (content: CourseContent) => content.attachments?.[0]?.url || "";
const resolveMediaTokenUrl = (content: CourseContent) => {
  if (content.type === 4) {
    return content?.external_media?.media_token
  } else {
    return content.external_media?.media_token
      ? `${STORAGE_PREVIEW_BASE}/api/media/file?token=${content.external_media.media_token}`
      : "";
  }

}


export default function ContentView({ content, lessonTitle, analyticsContext }: Props) {
  const attachment = content.attachments?.[0];
  const attachmentUrl = resolveAttachmentUrl(content);
  const mediaToken = content.external_media?.media_token || "";
  const mediaTokenUrl = resolveMediaTokenUrl(content);
  const externalMediaUrl = content.external_media?.media_url || "";
  const fileUrl = attachmentUrl || mediaTokenUrl || externalMediaUrl;
  const audioTitle = lessonTitle || attachment?.file_name || content.type_label || `جلسه ${content.id}`;


  const isVideo = content.type === 2;
  const analyticsMeta =
    isVideo && analyticsContext?.miniappId
      ? {
        miniappId: analyticsContext.miniappId,
        courseId: analyticsContext.courseId,
        lessonId: analyticsContext.lessonId,
      }
      : undefined;
  const textHtml = useMemo(() => content.text_content || "", [content.text_content]);

  return (
    <div dir="rtl" className={isVideo ? "w-full" : ""}>
      {content.type === 0 ? (
        <div
          style={{ whiteSpace: "pre-wrap", direction: "rtl", textAlign: "right" }}
          className="wrap-break-word overflow-hidden text-right text-sm leading-7 eitaamooz-editor"
          dangerouslySetInnerHTML={{ __html: textHtml }}
        />
      ) : null}

      {content.type === 1 ? (
        fileUrl ? (
          <AuthorizedImage src={fileUrl} alt={content.type_label || "image-content"} className="w-full rounded-lg" />
        ) : (
          <p className="text-xs text-default-500">تصویری برای نمایش وجود ندارد.</p>
        )
      ) : null}

      {content.type === 2 ? (
        mediaToken ? (
          <AmoozimPlayer payload={mediaToken} id={String(content.id)} analytics={analyticsMeta} />
        ) : fileUrl ? (
          <AmoozimPlayer src={fileUrl} id={String(content.id)} disableAuthHeaders analytics={analyticsMeta} />
        ) : (
          <p className="text-xs text-default-500">ویدیویی برای نمایش وجود ندارد.</p>
        )
      ) : null}

      {content.type === 3 ? (
        mediaToken ? (
          <AudioPlayer id={String(content.id)} payload={mediaToken} title={audioTitle} />
        ) : fileUrl ? (
          <AudioPlayer id={String(content.id)} src={fileUrl} title={audioTitle} />
        ) : (
          <p className="text-xs text-default-500">فایل صوتی برای نمایش وجود ندارد.</p>
        )
      ) : null}

      {content.type === 4 ? (
        fileUrl ? (
          <>
            <LessonFile
              content={content}
              url={fileUrl}
            />
          </>
        ) : (
          <p className="text-xs text-default-500">فایلی برای نمایش وجود ندارد.</p>
        )
      ) : null}
    </div>
  );
}
